import { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Bug, HelpCircle, Lightbulb, Heart, MessageSquare, X, Send, ImageIcon, Loader2, MessageSquareText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TYPES = [
  { value: "bug", label: "Bug", desc: "Something broke or looks wrong", Icon: Bug, color: "clay" },
  { value: "confusing", label: "Confusing", desc: "I couldn't figure something out", Icon: HelpCircle, color: "gold" },
  { value: "suggestion", label: "Suggestion", desc: "An idea to make it better", Icon: Lightbulb, color: "sage" },
  { value: "praise", label: "Praise", desc: "Something I loved", Icon: Heart, color: "forest" },
  { value: "other", label: "Other", desc: "Anything else on your mind", Icon: MessageSquare, color: "moss" },
];

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  // On mobile, the floating button overlaps the message composer's send
  // control on Messages and Coffee-Chats routes. Hide it on mobile there
  // only; desktop placement stays exactly as before.
  const inMessagingRoute =
    pathname.startsWith("/messages") ||
    pathname.startsWith("/coffee-chats") ||
    // Individual project detail (`/jobs/{id}` and `/jobs/{id}/edit`) — the
    // bottom-of-page Manuscript section would otherwise be covered by the
    // floating button on narrow screens. The `/jobs` list route keeps the
    // FAB because it doesn't have any bottom-of-viewport interactive area.
    /^\/jobs\/[^/]+/.test(pathname);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="feedback-fab"
        aria-label="Report feedback"
        className={`fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-40 items-center gap-2 rounded-full bg-[hsl(var(--sb-forest))] text-white px-4 py-3 shadow-lg hover:shadow-xl hover:bg-[hsl(var(--sb-forest-2))] transition-all hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-gold))] focus:ring-offset-2 ${
          inMessagingRoute ? "hidden sm:flex" : "flex"
        }`}
      >
        <MessageSquareText size={18} />
        <span className="hidden sm:inline text-sm font-medium">Feedback</span>
      </button>
      {open && <FeedbackDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function FeedbackDialog({ onClose }) {
  const location = useLocation();
  const { user } = useAuth();
  const isAuthed = !!user;
  const [type, setType] = useState("bug");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileInputRef = useRef(null);

  const currentPage = `${location.pathname}${location.search || ""}`;

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(f.type)) {
      toast.error("Screenshot must be an image (jpg, png, webp, gif).");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Screenshot must be under 8 MB.");
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (description.trim().length < 4) {
      toast.error("Please add a short description (at least a few characters).");
      return;
    }
    setSubmitting(true);
    let screenshot_path = null;
    try {
      if (file && isAuthed) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
          const { data } = await api.post("/uploads", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          screenshot_path = data.path;
        } catch (upErr) {
          toast.warning("Screenshot upload failed — sending feedback without it.");
        } finally {
          setUploading(false);
        }
      }
      await api.post("/feedback", {
        type,
        description: description.trim(),
        page_url: currentPage,
        screenshot_path,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="feedback-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full sm:max-w-lg bg-card border border-border rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-border/60 bg-[hsl(var(--sb-forest)/0.03)]">
          <div className="min-w-0">
            <h2 className="font-serif text-2xl leading-tight">
              {done ? "Thank you!" : "Share your feedback"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {done
                ? "Your note is with the team. It really helps us tune Last Bookmark for launch."
                : "You’re helping shape Last Bookmark. Every note is read."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="feedback-close"
            aria-label="Close feedback"
            className="rounded-full p-2 hover:bg-muted text-muted-foreground shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="p-6 flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-[hsl(var(--sb-sage)/0.2)] text-[hsl(var(--sb-forest-2))] grid place-items-center">
              <Heart size={26} />
            </div>
            <p className="mt-4 text-sm max-w-sm">
              Thanks — your submission is queued in the admin dashboard. If it&apos;s a bug, we&apos;ll dig in quickly.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="feedback-done"
              className="btn-primary mt-6"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-5 overflow-y-auto">
            {/* Type selector */}
            <div>
              <div className="text-sm font-medium mb-2">What&apos;s this about?</div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {TYPES.map(({ value, label, Icon }) => {
                  const active = type === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setType(value)}
                      data-testid={`feedback-type-${value}`}
                      className={`flex flex-col items-center gap-1 rounded-xl border-2 px-2 py-3 text-xs font-medium transition-all ${
                        active
                          ? "border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-moss)/0.5)] text-[hsl(var(--sb-forest-2))]"
                          : "border-border bg-background hover:border-[hsl(var(--sb-forest)/0.5)] text-foreground/70"
                      }`}
                    >
                      <Icon size={18} />
                      {label}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {TYPES.find((t) => t.value === type)?.desc}
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium mb-1.5 block" htmlFor="feedback-description">
                Description
              </label>
              <textarea
                id="feedback-description"
                data-testid="feedback-description"
                rows={5}
                required
                minLength={4}
                maxLength={4000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  type === "bug"
                    ? "What went wrong? What were you trying to do?"
                    : type === "confusing"
                    ? "Which part didn't make sense?"
                    : type === "suggestion"
                    ? "Tell us what would make this better…"
                    : type === "praise"
                    ? "What did you love?"
                    : "Anything else you'd like to share…"
                }
                className="sb-input resize-none"
              />
              <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                <span>Current page: <code className="font-mono text-[11px]">{currentPage}</code></span>
                <span>{description.length}/4000</span>
              </div>
            </div>

            {/* Screenshot */}
            <div>
              <div className="text-sm font-medium mb-1.5">Screenshot (optional)</div>
              {!isAuthed ? (
                <div className="text-xs text-muted-foreground rounded-lg bg-muted/50 border border-border/60 px-3 py-2">
                  Sign in to attach a screenshot.
                </div>
              ) : file ? (
                <div className="rounded-xl border border-border p-3 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-[hsl(var(--sb-moss))] grid place-items-center text-[hsl(var(--sb-forest-2))]">
                    <ImageIcon size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{file.name}</div>
                    <div className="text-xs text-muted-foreground">{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                    data-testid="feedback-remove-screenshot"
                    className="text-sm text-muted-foreground hover:underline"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border px-3 py-4 cursor-pointer hover:border-[hsl(var(--sb-forest)/0.5)] text-sm text-muted-foreground">
                  <ImageIcon size={16} />
                  Attach a screenshot (jpg, png, webp, gif · up to 8 MB)
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleFileChange}
                    data-testid="feedback-screenshot-input"
                  />
                </label>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-1 border-t border-border/60 -mx-5 -mb-5 px-5 py-4 bg-[hsl(var(--sb-forest)/0.02)]">
              <button
                type="button"
                onClick={onClose}
                data-testid="feedback-cancel"
                className="btn-ghost"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || uploading}
                data-testid="feedback-submit"
                className="btn-primary inline-flex items-center gap-2"
              >
                {submitting || uploading ? (
                  <><Loader2 size={16} className="animate-spin" /> {uploading ? "Uploading…" : "Sending…"}</>
                ) : (
                  <><Send size={16} /> Send feedback</>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
